/**
 * parseParkedSequenceStatement — lightweight lexical scan that pulls a
 * sequence name out of the parked statement text, plus best-effort numeric
 * fields for seeding the popover.
 *
 * DSE2's `extractDdlStructure` does not yet expose a structured sequence arm
 * (DSE2 §3.D4 carries the planner but the extractor lands in a follow-up
 * DSE2 slice), so the inline `[Edit Sequence]` popover does its own scan.
 *
 * Accepts CREATE / ALTER SEQUENCE [IF NOT EXISTS] [schema.]name [<clauses>].
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D3 — Sequence UI.
 */
import type { QualifiedName } from '../../../sql-grammar/model';

const QUALIFIED_NAME_RE = /^([A-Za-z_][A-Za-z0-9_]*|"[^"]+"|`[^`]+`|\[[^\]]+\])(?:\s*\.\s*([A-Za-z_][A-Za-z0-9_]*|"[^"]+"|`[^`]+`|\[[^\]]+\]))?/;

export interface ParsedParkedSequence {
  readonly name: QualifiedName;
  readonly action: 'create' | 'alter';
  readonly startWith?: string;
  readonly incrementBy?: string;
  readonly minValue?: string;
  readonly maxValue?: string;
  readonly cycle?: 'YES' | 'NO';
  readonly cache?: string;
  readonly dataType?: string;
}

function unquoteIdent(raw: string): { name: string; quoted: boolean } {
  if (raw.length >= 2) {
    const first = raw.charAt(0);
    const last = raw.charAt(raw.length - 1);
    if ((first === '"' && last === '"') || (first === '`' && last === '`')) {
      return { name: raw.slice(1, -1), quoted: true };
    }
    if (first === '[' && last === ']') {
      return { name: raw.slice(1, -1), quoted: true };
    }
  }
  return { name: raw, quoted: false };
}

function buildQualifiedName(rawHead: string, rawTail: string | undefined): QualifiedName {
  if (rawTail !== undefined) {
    const head = unquoteIdent(rawHead);
    const tail = unquoteIdent(rawTail);
    return {
      schema: head.name,
      name: tail.name,
      raw: `${rawHead}.${rawTail}`,
      quoted: head.quoted || tail.quoted,
      foldedName: tail.name.toLowerCase(),
      foldedSchema: head.name.toLowerCase(),
    };
  }
  const ident = unquoteIdent(rawHead);
  return {
    name: ident.name,
    raw: rawHead,
    quoted: ident.quoted,
    foldedName: ident.name.toLowerCase(),
  };
}

function findClause(upper: string, keyword: string, startIdx: number): number {
  const re = new RegExp(`\\b(?:${keyword})\\b`, 'g');
  re.lastIndex = startIdx;
  const m = re.exec(upper);
  return m ? m.index + m[0].length : -1;
}

function readNumberAfter(upper: string, original: string, idx: number): string | undefined {
  if (idx === -1) return undefined;
  let i = idx;
  while (i < upper.length && /\s/.test(upper.charAt(i))) i++;
  let end = i;
  let signed = false;
  if (upper.charAt(end) === '-' || upper.charAt(end) === '+') {
    end++;
    signed = true;
  }
  const digitsStart = end;
  while (end < upper.length && /[0-9]/.test(upper.charAt(end))) end++;
  if (end === digitsStart) return undefined;
  return original.slice(signed ? i : digitsStart, end);
}

function readDataTypeAfter(upper: string, original: string, idx: number): string | undefined {
  if (idx === -1) return undefined;
  let i = idx;
  while (i < upper.length && /\s/.test(upper.charAt(i))) i++;
  let end = i;
  while (end < upper.length && /[A-Za-z0-9_(),]/.test(upper.charAt(end))) end++;
  if (end === i) return undefined;
  return original.slice(i, end);
}

export function parseParkedSequenceStatement(stmtText: string): ParsedParkedSequence | null {
  const trimmed = stmtText.trim();
  const upper = trimmed.toUpperCase();
  let cursor = 0;
  let action: 'create' | 'alter';
  if (upper.startsWith('CREATE')) { action = 'create'; cursor = 6; }
  else if (upper.startsWith('ALTER')) { action = 'alter'; cursor = 5; }
  else return null;

  while (cursor < trimmed.length && /\s/.test(trimmed.charAt(cursor))) cursor++;

  // Optional IF NOT EXISTS / IF EXISTS prefix on CREATE / ALTER.
  if (upper.startsWith('IF', cursor)) {
    const ifClause = upper.slice(cursor);
    const m = /^IF\s+(NOT\s+)?EXISTS\s+/.exec(ifClause);
    if (m) cursor += m[0].length;
  }

  if (!upper.startsWith('SEQUENCE', cursor)) return null;
  cursor += 'SEQUENCE'.length;
  while (cursor < trimmed.length && /\s/.test(trimmed.charAt(cursor))) cursor++;

  // Some engines (mssql) accept `IF NOT EXISTS` after SEQUENCE keyword.
  if (upper.startsWith('IF', cursor)) {
    const ifClause = upper.slice(cursor);
    const m = /^IF\s+(NOT\s+)?EXISTS\s+/.exec(ifClause);
    if (m) cursor += m[0].length;
  }

  const remaining = trimmed.slice(cursor);
  const match = QUALIFIED_NAME_RE.exec(remaining);
  if (match === null) return null;
  const name = buildQualifiedName(match[1]!, match[2]);
  cursor += match[0].length;

  const tailUpper = upper.slice(cursor);
  const tailOriginal = trimmed.slice(cursor);
  const startIdx = findClause(tailUpper, 'START\\s+WITH', 0);
  const incIdx = findClause(tailUpper, 'INCREMENT\\s+BY', 0);
  const minIdx = findClause(tailUpper, 'MINVALUE', 0);
  const noMinIdx = findClause(tailUpper, 'NO\\s+MINVALUE|NOMINVALUE', 0);
  const maxIdx = findClause(tailUpper, 'MAXVALUE', 0);
  const noMaxIdx = findClause(tailUpper, 'NO\\s+MAXVALUE|NOMAXVALUE', 0);
  const cacheIdx = findClause(tailUpper, 'CACHE', 0);
  const noCacheIdx = findClause(tailUpper, 'NO\\s+CACHE|NOCACHE', 0);
  const cycleIdx = findClause(tailUpper, 'CYCLE', 0);
  const noCycleIdx = findClause(tailUpper, 'NO\\s+CYCLE|NOCYCLE', 0);
  const asIdx = findClause(tailUpper, 'AS', 0);

  const seq: ParsedParkedSequence = {
    name,
    action,
    startWith: readNumberAfter(tailUpper, tailOriginal, startIdx),
    incrementBy: readNumberAfter(tailUpper, tailOriginal, incIdx),
    minValue: noMinIdx !== -1
      ? 'NONE'
      : readNumberAfter(tailUpper, tailOriginal, minIdx),
    maxValue: noMaxIdx !== -1
      ? 'NONE'
      : readNumberAfter(tailUpper, tailOriginal, maxIdx),
    cache: noCacheIdx !== -1
      ? 'NONE'
      : readNumberAfter(tailUpper, tailOriginal, cacheIdx),
    cycle: noCycleIdx !== -1 ? 'NO' : (cycleIdx !== -1 ? 'YES' : undefined),
    dataType: readDataTypeAfter(tailUpper, tailOriginal, asIdx),
  };
  return seq;
}
